import { ScrollView, StyleSheet, Dimensions, View, Platform, KeyboardAvoidingView } from 'react-native';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { TextInput, Button, Surface, useTheme, Text, Portal, Modal, Menu } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useEffect, useState, useRef } from 'react';
import { requestPasswordReset, requestMagicLink, getUserDetails } from '../src/api/apiService';
import { clearOwnerDriverStorage } from '../src/features/owner/ownerDriverPrompt';
import { getItem, setItem, removeItem } from '../src/lib/storage';
import Logo from '../src/components/Logo';
import * as Animatable from 'react-native-animatable';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { setStoredLanguage, getStoredLanguage } from '../src/lib/i18n';
import * as Sentry from '@sentry/react-native';
import Constants from 'expo-constants';
import { API_BASE } from '../src/api/config';
import { LoadingScreen } from '../src/components/LoadingScreen';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useAuth } from '../src/context/AuthContext';
import { getLoginErrorMessage } from '../src/utils/loginErrorMessage';
import { getOnboardingRedirectForLogin } from '../src/utils/onboardingRedirect';
import { partnerHomeRoute } from '../src/utils/partnerHome';
import { track } from '../src/lib/analytics';
import { AnalyticsEvent } from '../src/lib/analyticsEvents';

const { width: screenWidth } = Dimensions.get('window');

export default function LoginScreen() {
  const { t, i18n: i18nInstance } = useTranslation(["login", "translation", "register"]);
  const router = useRouter();
  const params = useLocalSearchParams();
  const theme = useTheme();
  const { login: authLogin } = useAuth(); // Use AuthContext login method
  const [isCheckingToken, setIsCheckingToken] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetMessage, setResetMessage] = useState<string | null>(null);
  const [emailConfirmed, setEmailConfirmed] = useState<boolean>(false);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const hasShownConfirmation = useRef<boolean>(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const loginSchema = z.object({
    email: z
      .string()
      .min(1, t('translation:validation.required'))
      .email(t('translation:validation.invalidEmail')),
    password: z
      .string()
      .min(1, t('translation:validation.required'))
      .min(8, t('translation:validation.passwordMinLength')),
  });

  const passwordResetSchema = z.object({
    email: z
      .string()
      .min(1, t('translation:validation.required'))
      .email(t('translation:validation.invalidEmail')),
  });

  type LoginFormData = z.infer<typeof loginSchema>;
  type PasswordResetFormData = z.infer<typeof passwordResetSchema>;

  const changeLanguage = async (language: string) => {
    try {
      // Store the language in persistent settings
      await setStoredLanguage(language);
      // Change the language in i18n
      await i18nInstance.changeLanguage(language);
      setShowLanguageMenu(false);
    } catch (error) {
      console.error('Error saving language preference:', error);
    }
  };


  const getCurrentLanguage = () => {
    switch (i18nInstance.language) {
      case 'pt':
        return '🇵🇹 PT';
      case 'pt_BR':
        return '🇧🇷 BR';
      default:
        return '🇬🇧 EN';
    }
  };

  const [isLoggingIn, setIsLoggingIn] = useState(false); // Local loading state for immediate feedback
  const [isRequestingMagicLink, setIsRequestingMagicLink] = useState(false); // Passwordless magic-link request (#205)

  const {
    control: loginControl,
    handleSubmit: handleLoginSubmit,
    setError: setLoginError,
    reset: resetLogin,
    getValues: getLoginValues,
    formState: { errors: loginErrors, isSubmitting: isLoginSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      // invite_email: prefill from a pending invitation link.
      // prefill_email: prefill after a duplicate NIF/NIPC/CMTVDE registration
      // conflict, so the user can log in with the account that already
      // owns that identifier instead of re-typing their email.
      email: (params as any).invite_email ?? (params as any).prefill_email ?? '',
      password: '',
    },
  });

  const {
    control: resetControl,
    handleSubmit: handleResetSubmit,
    setError: setResetError,
    reset: resetResetForm,
    formState: { errors: resetErrors, isSubmitting: isResetSubmitting },
  } = useForm<PasswordResetFormData>({
    resolver: zodResolver(passwordResetSchema),
    defaultValues: {
      email: '',
    },
  });

  useEffect(() => {
    if ((params as any).open_recovery === 'true') {
      resetResetForm({ email: (params as any).prefill_email ?? '' });
      setShowResetModal(true);
    }
  }, [params, resetResetForm]);

  // Separate effect for handling email confirmation
  useEffect(() => {
    if (params.email_confirmed === 'true' && !hasShownConfirmation.current) {
      hasShownConfirmation.current = true;
      setEmailConfirmed(true);

      // Clear any existing timer
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      // Set new timer
      timerRef.current = setTimeout(() => {
        setEmailConfirmed(false);
        hasShownConfirmation.current = false;
      }, 5000);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [params.email_confirmed]);

  // Separate effect for token check
  useEffect(() => {
    const checkToken = async () => {
      try {
        const token = await getItem('userToken');
        if (token) {
          const userDetailsResponse = await getUserDetails();

          if (userDetailsResponse.ok) {
            const inviteToken = (params as any).invite_token;
            if (inviteToken) {
              router.replace(`/invitations/accept/${encodeURIComponent(String(inviteToken))}/`);
              return;
            }
            // Bot verification deep link takes priority over onboarding redirect
            const pendingBotCode = await getItem('pendingBotVerifyCode');
            if (pendingBotCode) {
              await setItem('pendingBotVerifyCode', '');
              router.replace(`/bot-verify?code=${pendingBotCode}`);
            } else {
              // A10b-1: a partner-only account has no Owner/Driver dashboard.
              const partnerRoute = await partnerHomeRoute(userDetailsResponse.data);
              if (partnerRoute) {
                router.replace(partnerRoute);
                return;
              }
              const redirectResult = await getOnboardingRedirectForLogin(userDetailsResponse.data);

              if (redirectResult.shouldRedirect && redirectResult.route) {
                router.replace(redirectResult.route);
              } else {
                router.replace('/dashboard/home');
              }
            }
          } else {
            // Token invalid/expired — clear it to prevent auth check loop
            await Promise.all([
              removeItem('userToken'),
              clearOwnerDriverStorage(),
            ]);
            router.replace('/');
          }
        }
      } catch (error) {
        console.error('[LoginScreen] Erro ao verificar token:', error);
      } finally {
        setIsCheckingToken(false);
      }
    };

    checkToken();
  }, [router]);

  // Add effect to handle reset status
  useEffect(() => {
    if (params.reset) {
      switch (params.reset) {
        case 'success':
          setResetMessage(t('passwordResetSuccess'));
          break;
        case 'invalid':
          setResetMessage(t('passwordResetInvalid'));
          break;
        case 'error':
          setResetMessage(t('passwordResetError'));
          break;
      }

      setTimeout(() => {
        setResetMessage(null);
      }, 5000);
    }
  }, [params.reset]);

  // Canonical onboarding outcome surfaced after login when accepting an
  // invitation ends pending (contract H / #345): the driver must SEE the
  // pending state, not silently land on the generic redirect.
  const [inviteOutcome, setInviteOutcome] = useState<{
    companyName: string;
    outcome: 'pending_no_license' | 'failed';
  } | null>(null);

  const continueAfterLogin = async () => {
    resetLogin();

    // Get user details to check groups and companies
    const userDetailsResponse = await getUserDetails();
    if (userDetailsResponse.ok) {
      const userData = userDetailsResponse.data;

      // Bot verification deep link takes priority over onboarding redirect
      const pendingBotCode = await getItem('pendingBotVerifyCode');
      if (pendingBotCode) {
        await setItem('pendingBotVerifyCode', '');
        router.replace(`/bot-verify?code=${pendingBotCode}`);
      } else {
        // A10b-1: a partner-only account has no Owner/Driver dashboard.
        const partnerRoute = await partnerHomeRoute(userData);
        if (partnerRoute) {
          router.replace(partnerRoute);
          return;
        }
        const redirectResult = await getOnboardingRedirectForLogin(userData);

        if (redirectResult.shouldRedirect && redirectResult.route) {
          router.replace(redirectResult.route);
        } else {
          router.replace('/dashboard/home');
        }
      }
    } else {
      // If dashboard/user details are unavailable, stay on login
      router.replace('/');
    }
  };

  const handleLogin: SubmitHandler<LoginFormData> = async (data) => {
    setIsLoggingIn(true);
    try {
      const result = await authLogin(data.email, data.password);

      if (!result.success) {
        const errorMessage = getLoginErrorMessage(result.response, t);

        setLoginError('email', { type: 'server', message: errorMessage });
        return;
      }

      // Authentication is not invitation consent; show the authenticated recipient first.
      const inviteToken = (params as any).invite_token;
      if (inviteToken) {
        router.replace(`/invitations/accept/${encodeURIComponent(String(inviteToken))}/`);
        return;
      }
      await continueAfterLogin();
    } catch (error) {
      console.error('[Login] Erro na requisição:', error);
      setLoginError('password', {
        type: 'server',
        message: t('loginError'),
      });
    } finally {
      setIsLoggingIn(false); // Always clear loading state
    }
  };

  const handlePasswordReset: SubmitHandler<PasswordResetFormData> = async (data) => {
    try {
      const response = await requestPasswordReset(data.email);

      if (!response.ok) {
        const errorData = response.data as any;
        const errorMessage =
          errorData.email?.[0] ||
          errorData.detail ||
          t('passwordReset.failed');
        setResetError('email', { type: 'server', message: errorMessage });
        return;
      }

      setResetMessage(t('passwordReset.success'));
      resetResetForm();
      setTimeout(() => {
        setShowResetModal(false);
        setResetMessage(null);
      }, 3000);
    } catch (error) {
      console.error('[PasswordReset] Erro na requisição:', error);
      setResetError('email', {
        type: 'server',
        message: t('passwordReset.error'),
      });
    }
  };

  // Passwordless magic-link (#205): request a single-use email link using the
  // email already typed in the login form, then route to the neutral
  // "check your email" confirmation screen. The backend response is always
  // generic (no account enumeration); we only surface an error on a real
  // network/transport failure so the user is never left in limbo.
  const handleMagicLinkRequest = async () => {
    const email = (getLoginValues('email') || '').trim().toLowerCase();
    if (!email || !z.string().email().safeParse(email).success) {
      setLoginError('email', {
        type: 'manual',
        message: t('login:magicLink.emailRequired'),
      });
      return;
    }
    setIsRequestingMagicLink(true);
    try {
      const response = await requestMagicLink(email);
      if (!response.ok) {
        setLoginError('email', {
          type: 'server',
          message: t('login:magicLink.requestError'),
        });
        return;
      }
      router.push(`/magic-link-sent?email=${encodeURIComponent(email)}`);
    } catch (error) {
      console.error('[MagicLink] Erro na requisição:', error);
      setLoginError('email', {
        type: 'server',
        message: t('login:magicLink.requestError'),
      });
    } finally {
      setIsRequestingMagicLink(false);
    }
  };

  if (isCheckingToken) {
    return <LoadingScreen />;
  }

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: theme.colors.background }}>
      <View style={styles.languageSelector}>
        <Menu
          visible={showLanguageMenu}
          onDismiss={() => setShowLanguageMenu(false)}
          anchor={
            <Button
              mode="text"
              onPress={() => setShowLanguageMenu(true)}
              style={styles.languageButton}
            >
              {getCurrentLanguage()}
            </Button>
          }
        >
          <Menu.Item
            onPress={() => changeLanguage('en')}
            title={t('translation:languages.english')}
            style={styles.menuItem}
          />
          <Menu.Item
            onPress={() => changeLanguage('pt')}
            title={t('translation:languages.portuguese')}
            style={styles.menuItem}
          />
          <Menu.Item
            onPress={() => changeLanguage('pt_BR')}
            title={t('translation:languages.portugueseBR')}
            style={styles.menuItem}
          />
        </Menu>
      </View>

      <KeyboardAvoidingView 
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardAvoidingView}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}
      >
        <ScrollView 
          contentContainerStyle={styles.scrollContainer}
          keyboardShouldPersistTaps="handled"
        >
        <Surface style={styles.surface} mode="elevated" elevation={1}>
          <Logo size="large" style={styles.logo} />

          {emailConfirmed && (
            <Animatable.View 
              animation="fadeIn" 
              duration={800}
              style={styles.confirmationMessage}
            >
              <View style={[styles.confirmationContainer, { backgroundColor: theme.colors.primary }]}>
                <MaterialCommunityIcons name="check-circle" size={24} color="white" />
                <Text style={styles.confirmationText}>{t('login:emailVerifiedSuccessfully')}</Text>
              </View>
            </Animatable.View>
          )}

          <Text style={{ ...theme.fonts.titleLarge }}>{t('login:loginTitle')}</Text>

          <Controller
            control={loginControl}
            name="email"
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <View style={styles.inputContainer}>
                <TextInput
                  testID="login-email"
                  label={t('login:emailPlaceholder')}
                  mode="outlined"
                  autoCapitalize="none"
                  keyboardType="email-address"
                  onBlur={onBlur}
                  onChangeText={(text) => onChange(text.toLowerCase())}
                  value={value}
                  error={!!error}
                  style={styles.input}
                  disabled={isLoginSubmitting}
                />
                {error && <Text style={{ color: theme.colors.error }}>{error.message}</Text>}
              </View>
            )}
          />

          <Controller
            control={loginControl}
            name="password"
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <View style={styles.inputContainer}>
                <TextInput
                  testID="login-password"
                  label={t('login:passwordPlaceholder')}
                  mode="outlined"
                  secureTextEntry={!showPassword}
                  autoCapitalize="none"
                  onBlur={onBlur}
                  onChangeText={onChange}
                  value={value}
                  error={!!error}
                  style={styles.input}
                  disabled={isLoginSubmitting}
                  right={
                    <TextInput.Icon
                      icon={showPassword ? 'eye-off' : 'eye'}
                      onPress={() => setShowPassword(!showPassword)}
                    />
                  }
                />
                {error && <Text style={{ color: theme.colors.error }}>{error.message}</Text>}
              </View>
            )}
          />

          <Button
            mode="contained"
            onPress={handleLoginSubmit(handleLogin)}
            loading={isLoggingIn || isLoginSubmitting}
            disabled={isLoggingIn || isLoginSubmitting}
            style={styles.button}
            // Explicit hook: since #205 added the magic-link button, a
            // name-based /Sign in|Login|Entrar/ lookup matches two elements.
            testID="login-submit"
          >
            {t('login:loginButton')}
          </Button>

          {/* Forgot Password Link */}
          <Button
            mode="text"
            onPress={() => setShowResetModal(true)}
            style={styles.forgotPasswordButton}
            disabled={isLoggingIn || isLoginSubmitting}
          >
            {t('login:forgotPassword')}
          </Button>

          {/* Passwordless magic-link entry point (#205) */}
          <Button
            mode="text"
            icon="email-fast-outline"
            onPress={handleMagicLinkRequest}
            loading={isRequestingMagicLink}
            style={styles.forgotPasswordButton}
            disabled={isLoggingIn || isLoginSubmitting || isRequestingMagicLink}
            testID="login-magic-link"
            accessibilityRole="button"
            accessibilityLabel={t('login:magicLink.cta')}
          >
            {t('login:magicLink.cta')}
          </Button>

          <Surface style={styles.footer} elevation={0}>
            <Text style={{ ...theme.fonts.default }}>{t('login:signupPrompt')}</Text>
            <Button onPress={() => router.push('/register/choice')}>
              {t('login:signupLink')}
            </Button>
          </Surface>
          
          <Surface style={styles.privacyFooter} elevation={0}>
            <Text 
              style={[{ color: theme.colors.primary }, styles.privacyLink]} 
              onPress={() => router.push('/privacy')}
            >
              {t('login:privacyInfo.privacyLink')}
            </Text>
          </Surface>
        </Surface>
      </ScrollView>
      </KeyboardAvoidingView>

      {/* Password Reset Modal */}
      <Portal>
        <Modal
          visible={showResetModal}
          onDismiss={() => {
            setShowResetModal(false);
            setResetMessage(null);
            resetResetForm();
          }}
          contentContainerStyle={[styles.modal, { backgroundColor: theme.colors.surface }]}
        >
          <Text style={{ ...theme.fonts.titleMedium, marginBottom: 20 }}>
            {t('login:resetPasswordTitle')}
          </Text>

          {resetMessage ? (
            <Text style={{ ...theme.fonts.bodyMedium, color: theme.colors.primary }}>
              {resetMessage}
            </Text>
          ) : (
            <>
              <Controller
                control={resetControl}
                name="email"
                render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                  <View style={styles.inputContainer}>
                    <TextInput
                      label={t('login:emailPlaceholder')}
                      mode="outlined"
                      autoCapitalize="none"
                      keyboardType="email-address"
                      onBlur={onBlur}
                      onChangeText={(text) => onChange(text.toLowerCase())}
                      value={value}
                      error={!!error}
                      style={styles.input}
                      disabled={isResetSubmitting}
                    />
                    {error && <Text style={{ color: theme.colors.error }}>{error.message}</Text>}
                  </View>
                )}
              />

              <Button
                mode="contained"
                onPress={handleResetSubmit(handlePasswordReset)}
                loading={isResetSubmitting}
                disabled={isResetSubmitting}
                style={styles.button}
              >
                {t('login:resetPasswordButton')}
              </Button>
            </>
          )}
        </Modal>

        {/* Canonical invitation outcome (contract H / #345): shown when the
            post-login invitation accept ends pending — never a silent redirect. */}
        <Modal
          visible={!!inviteOutcome}
          onDismiss={() => {
            setInviteOutcome(null);
            continueAfterLogin();
          }}
          contentContainerStyle={[styles.modal, { backgroundColor: theme.colors.surface }]}
        >
          <Text style={{ ...theme.fonts.titleMedium, marginBottom: 12 }} testID="invite-outcome-title">
            {inviteOutcome
              ? t(
                  inviteOutcome.outcome === 'pending_no_license'
                    ? 'register:outcomePendingLicenseTitle'
                    : 'register:outcomeFailedTitle',
                )
              : ''}
          </Text>
          <Text style={{ ...theme.fonts.bodyMedium, marginBottom: 20 }} testID="invite-outcome-message">
            {inviteOutcome
              ? t(
                  inviteOutcome.outcome === 'pending_no_license'
                    ? 'register:outcomePendingLicenseMessage'
                    : 'register:outcomeFailedMessage',
                  { company: inviteOutcome.companyName },
                )
              : ''}
          </Text>
          <Button
            mode="contained"
            onPress={() => {
              setInviteOutcome(null);
              continueAfterLogin();
            }}
            style={styles.button}
            testID="invite-outcome-continue"
          >
            {t('register:outcomeContinue')}
          </Button>
        </Modal>
      </Portal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  keyboardAvoidingView: {
    flex: 1,
  },
  scrollContainer: {
    padding: 10,
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  surface: {
    width: screenWidth <= 480 ? '90%' : screenWidth <= 768 ? '60%' : '40%',
    padding: 20,
    borderRadius: 8,
  },
  logo: {
    width: 250,
    height: 200,
    alignSelf: 'center',
    marginBottom: 20,
  },
  confirmationMessage: {
    width: '100%',
    marginBottom: 20,
  },
  confirmationContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 8,
    justifyContent: 'center',
  },
  confirmationText: {
    color: 'white',
    marginLeft: 8,
    fontWeight: '500',
  },
  inputContainer: {
    marginBottom: 10,
  },
  input: {
    width: '100%',
  },
  button: {
    marginTop: 10,
  },
  forgotPasswordButton: {
    marginTop: 10,
    alignSelf: 'center',
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 20,
  },
  modal: {
    padding: 20,
    margin: 20,
    borderRadius: 8,
    ...(Platform.OS === 'web' ? {
      maxWidth: 500,
      width: '90%',
      alignSelf: 'center',
    } : {}),
  },
  privacyFooter: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 10,
  },
  privacyLink: {
    fontSize: 14,
    textDecorationLine: 'underline',
  },
  testButton: {
    marginTop: 20,
  },
  registrationOptions: {
    width: '100%',
    alignItems: 'center',
    gap: 10,
  },
  ownerRegistrationContainer: {
    alignItems: 'center',
    marginTop: 10,
  },
  comingSoonButton: {
    opacity: 0.6,
  },
  contactText: {
    fontSize: 12,
    textAlign: 'center',
    marginTop: 5,
  },
  emailLink: {
    fontSize: 12,
    textDecorationLine: 'underline',
    marginTop: 2,
  },
  languageSelector: {
    alignItems: 'flex-end',
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  languageButton: {
    minWidth: 80,
  },
  menuItem: {
    paddingVertical: 8,
  },
});